import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { FaEye, FaEdit, FaTrash, FaSearch, FaTimes, FaPlus } from 'react-icons/fa';
import { useDispatch, useSelector } from "react-redux";
import { successToast, errorToast } from '../../Utils/toast';
import { openModal } from "../../redux/features/modalSlice";
import Pagination from '../../common/Pagination';
import SearchBar from '../../common/Search';
import { setGaonFilters, resetGaonFilters } from '../../redux/features/filtersSlice';
import { useGetGaonQuery } from "../../redux/services/gaonService/gaon.Services";
import { useGetWaadDropdownQuery } from "../../redux/services/waadService/waad.Services";
import CommonBreadcrumb from '../../common/CommonBreadcrumb';
function Gaon() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // ✅ Get gaon filters from Redux
    const gaonFilters = useSelector((state) => state.filters.gaon);
    
    // Local state for UI
    const [statusFilter, setStatusFilter] = useState(gaonFilters.status || '');
    const [waadFilter, setWaadFilter] = useState(gaonFilters.waad_id || '');
    const [searchTerm, setSearchTerm] = useState(gaonFilters.search || '');

    // ✅ Use gaonFilters for API call
    const { data, isLoading, isError, refetch } = useGetGaonQuery(gaonFilters);
    const { data: waadData } = useGetWaadDropdownQuery();

    const gaonData = data?.data || [];
    const pagination = data?.pagination || {};
    const totalPages = pagination?.totalPages || 0;
    const totalItems = pagination?.total || 0;
    const currentPage = pagination?.page || 1;
    const showPagination = totalPages > 1;

    // ✅ Status check - 1 = Active, 0 = Inactive
    const isActive = (status) => {
        return status === 1 || status === "1" || status === true;
    };

    // ✅ Sync local state with Redux when filters change
    useEffect(() => {
        setStatusFilter(gaonFilters.status || '');
        setWaadFilter(gaonFilters.waad_id || '');
        setSearchTerm(gaonFilters.search || '');
    }, [gaonFilters]);

    // Handle page change
    const handlePageChange = (page) => {
        dispatch(
            setGaonFilters({
                page: page,
            })
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Handle search
    const handleSearch = (value) => {
        setSearchTerm(value);
        dispatch(
            setGaonFilters({
                search: value,
                page: 1,
            })
        );
    };

    // Handle refresh
    const refetchData = () => {
        refetch();
        successToast("Data refreshed successfully!");
    };

    // Handle status filter
    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        dispatch(
            setGaonFilters({
                status: status,
                page: 1,
            })
        );
    };

    // Handle waad filter
    const handleWaadFilter = (waad_id) => {
        setWaadFilter(waad_id);
        dispatch(
            setGaonFilters({
                waad_id: waad_id,
                page: 1,
            })
        );
    };

    // ✅ Clear all filters using resetGaonFilters
    const clearFilters = () => {
        setStatusFilter('');
        setWaadFilter('');
        setSearchTerm('');
        dispatch(resetGaonFilters());
    };

    // Handle limit change
    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        dispatch(
            setGaonFilters({
                limit: newLimit,
                page: 1,
            })
        );
    };

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="mt-3">Loading Gaon data...</h5>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="text-center py-5">
                <div className="alert alert-danger">
                    <h5>Something Went Wrong</h5>
                    <p>Unable to load Gaon data. Please try again.</p>
                    <button className="btn btn-primary" onClick={refetchData}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
        <CommonBreadcrumb
    title={
        <>
            All Gaon
            {totalItems > 0 && (
                <span className="badge-custom bg-primary">
                    {totalItems}
                </span>
            )}
        </>
    }
    breadcrumbs={[
        { label: "Home", link: "/" },
        { label: "All Gaon" },
    ]}
    buttons={[
        {
            text: "Add Gaon",
            icon: <FaPlus className="me-1" />,
            link: "/add-gaon",
            className: "btn btn-primary",
        },
    ]}
/>

            {/* SEARCH AND FILTER SECTION */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-center">
                        {/* Search Bar */}
                        <div className="col-md-3">
                            <SearchBar
                                search={searchTerm}
                                setSearch={handleSearch}
                                placeholder="Search Gaon by name..."
                            />
                        </div>

                        {/* Waad Filter */}
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={waadFilter}
                                onChange={(e) => handleWaadFilter(e.target.value)}
                            >
                                <option value="">All Waads</option>
                                {waadData?.data?.map((waad) => (
                                    <option key={waad.id} value={waad.id}>
                                        {waad.waad_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Status Filter - 1 = Active, 0 = Inactive */}
                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => handleStatusFilter(e.target.value)}
                            >
                                <option value="">All Status</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>

                        {/* Limit Filter */}
                        <div className="col-md-2">
                            <select
                                className="form-select"
                                value={gaonFilters.limit || 10}
                                onChange={handleLimitChange}
                            >
                                <option value={5}>5 per page</option>
                                <option value={10}>10 per page</option>
                                <option value={20}>20 per page</option>
                                <option value={50}>50 per page</option>
                                <option value={100}>100 per page</option>
                            </select>
                        </div>

                        {/* Action Buttons */}
                        <div className="col-md-2 text-md-end">
                            {(gaonFilters.search || gaonFilters.status || gaonFilters.waad_id) && (
                                <button
                                    className="btn btn-outline-secondary me-2"
                                    onClick={clearFilters}
                                >
                                    <FaTimes className="me-1" />
                                    Clear
                                </button>
                            )}
                            <button
                                className="btn btn-outline-primary"
                                onClick={refetchData}
                            >
                                Refresh
                            </button>
                        </div>
                    </div>

                    {/* Active Filters Display */}
                    {(gaonFilters.search || gaonFilters.status || gaonFilters.waad_id) && (
                        <div className="mt-3 d-flex flex-wrap gap-2">
                            <span className="text-muted me-2">Active Filters:</span>
                            {gaonFilters.search && (
                                <span className="badge bg-primary d-flex align-items-center gap-1">
                                    Search: {gaonFilters.search}
                                    <button
                                        className="btn btn-link btn-sm text-white p-0 ms-1"
                                        onClick={() => handleSearch('')}
                                    >
                                        <FaTimes />
                                    </button>
                                </span>
                            )}
                            {gaonFilters.waad_id && (
                                <span className="badge bg-info d-flex align-items-center gap-1">
                                    Waad: {waadData?.data?.find(w => w.id == gaonFilters.waad_id)?.waad_name || gaonFilters.waad_id}
                                    <button
                                        className="btn btn-link btn-sm text-white p-0 ms-1"
                                        onClick={() => handleWaadFilter('')}
                                    >
                                        <FaTimes />
                                    </button>
                                </span>
                            )}
                            {gaonFilters.status && (
                                <span className="badge bg-warning d-flex align-items-center gap-1">
                                    Status: {gaonFilters.status === '1' ? 'Active' : 'Inactive'}
                                    <button
                                        className="btn btn-link btn-sm text-white p-0 ms-1"
                                        onClick={() => handleStatusFilter('')}
                                    >
                                        <FaTimes />
                                    </button>
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* TABLE */}
            <div className="st-wrapper-card">
                {gaonData.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="mb-3">
                            <FaSearch className="text-muted" size={48} />
                        </div>
                        <h5>No Gaon Found</h5>
                        <p className="text-muted">
                            {gaonFilters.search || gaonFilters.status || gaonFilters.waad_id
                                ? "Try adjusting your search or filter criteria"
                                : "Click 'Add Gaon' to create your first gaon"}
                        </p>
                        {(gaonFilters.search || gaonFilters.status || gaonFilters.waad_id) && (
                            <button className="btn btn-primary" onClick={clearFilters}>
                                Clear Filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="st-table-responsive">
                        <table className="table st-custom-table align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Gaon Name</th>
                                    <th>Waad</th>
                                    <th>Population</th>
                                    <th>Pincode</th>
                                    <th>Status</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {gaonData.map((item, index) => (
                                    <tr key={item.id}>
                                        <td>
                                            {(currentPage - 1) * (gaonFilters.limit || 10) + index + 1}
                                        </td>
                                        <td className="fw-semibold">
                                            {item?.gaon_name || 'N/A'}
                                        </td>
                                        <td>{item?.waad_name || 'N/A'}</td>
                                        <td>{item?.population || 0}</td>
                                        <td>{item?.pincode || 'N/A'}</td>
                                        <td>
                                            <span className={`badge ${isActive(item.status) ? 'bg-success' : 'bg-danger'}`}>
                                                {isActive(item.status) ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            {/* VIEW */}
                                            <button
                                                className="st-action-btn st-view-btn me-1"
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            type: "view-gaon",
                                                            title: "View Gaon",
                                                            data: item,
                                                        })
                                                    )
                                                }
                                                title="View Details"
                                            >
                                                <FaEye />
                                            </button>

                                            {/* EDIT */}
                                            <button
                                                className="st-action-btn st-edit-btn me-1"
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            type: "edit-gaon",
                                                            title: "Edit Gaon",
                                                            data: item,
                                                        })
                                                    )
                                                }
                                                title="Edit Gaon"
                                            >
                                                <FaEdit />
                                            </button>

                                            {/* DELETE */}
                                            <button
                                                className="st-action-btn st-delete-btn"
                                                onClick={() =>
                                                    dispatch(
                                                        openModal({
                                                            type: "delete-gaon",
                                                            title: "Delete Gaon",
                                                            data: {
                                                                id: item.id,
                                                                name: item.gaon_name,
                                                                apiUrl: "/gaon-delete",
                                                                message: `Are you sure you want to delete "${item.gaon_name}"?`,
                                                                onSuccess: refetchData,
                                                            },
                                                        })
                                                    )
                                                }
                                                title="Delete Gaon"
                                            >
                                                <FaTrash />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* PAGINATION */}
                {showPagination && gaonData.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center justify-content-between mt-4">
                        <div className="text-muted small mb-2 mb-md-0">
                            Showing {((currentPage - 1) * (gaonFilters.limit || 10)) + 1} to{' '}
                            {Math.min(currentPage * (gaonFilters.limit || 10), totalItems)} of {totalItems} entries
                        </div>
                        <Pagination
                            page={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default Gaon;